import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { storage } from "../firebase/firebaseConfig";
import {
  ImageUploadErrorConverter,
  ImageUploadErrorCause,
} from "./converters/ImageUploadErrorConverter";
import { CoreError } from "../../domain/models/CoreError";

/**
 * ImageUploadService handles uploading images to Firebase Storage.
 * This service is dedicated only to image uploads and does not handle
 * any Realtime Database operations.
 */
export class ImageUploadService {
  // Singleton instance
  private static instance: ImageUploadService;

  // Private constructor to enforce singleton usage
  private constructor() {}

  // Global accessor to retrieve the singleton
  public static getInstance(): ImageUploadService {
    if (!ImageUploadService.instance) {
      ImageUploadService.instance = new ImageUploadService();
    }
    return ImageUploadService.instance;
  }

  /**
   * Uploads a profile photo to Firebase Storage.
   * The image will be stored at: profilePhotos/{userId}_1.jpg
   *
   * @param file The File object from an <input type="file"> element
   * @param userId The user's identifier (UID)
   * @returns Promise that resolves with the download URL of the uploaded image
   * @throws CoreError if the upload fails
   */
  public async uploadProfilePhoto(file: File, userId: string): Promise<string> {
    try {
      // Create reference to the storage location
      // Following the Swift implementation: profilePhotos/{userId}_1.jpg
      const imageRef = storageRef(storage, `profilePhotos/${userId}_1.jpg`);

      // Set metadata for the upload
      const metadata = {
        contentType: file.type || "image/jpeg",
      };

      // Upload the file
      await uploadBytes(imageRef, file, metadata);

      // Get the download URL
      const downloadURL = await getDownloadURL(imageRef);

      if (!downloadURL) {
        throw ImageUploadErrorConverter.createImageUploadError(
          ImageUploadErrorCause.DownloadURLNotFound,
          "Download URL is null after successful upload",
        );
      }

      return downloadURL;
    } catch (error) {
      if (error instanceof CoreError) {
        throw error;
      }
      throw ImageUploadErrorConverter.domainModel(error);
    }
  }
}
